import { Component, DestroyRef, ElementRef, Inject, inject, Renderer2, ViewContainerRef, DOCUMENT } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AlertDisplayComponent } from "./shared/components/alert-display/alert-display.component";
import { environment } from "../environments/environment";
import { Router, RouteConfigLoadStart, RouteConfigLoadEnd, NavigationEnd, RouterOutlet } from "@angular/router";
import { BusyService } from "./shared/services";
import { SubdomainService } from "./shared/services/subdomain.service";
import { Title } from "@angular/platform-browser";
import { DialogService } from "@ngneat/dialog";
import { getAreaFaviconPath } from "./shared/area-config";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],

    imports: [RouterOutlet, AlertDisplayComponent],
})
export class AppComponent {
    public isIframe = false;
    userClaimsUpsertStarted = false;
    ignoreSessionTerminated = false;

    public currentYear: number = new Date().getFullYear();
    public isHomePage: boolean = false;
    public isParcelTracker: boolean;
    public isEip: boolean;
    public isMainSite: boolean;
    private subdomain: string;

    private previouslyFocusedElement: HTMLElement | null = null;
    private destroyRef = inject(DestroyRef);

    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private router: Router,
        private busyService: BusyService,
        private titleService: Title,
        private renderer: Renderer2,
        public viewRef: ViewContainerRef,
        private elementRef: ElementRef,
        private dialogService: DialogService,
        subdomainService: SubdomainService
    ) {
        this.subdomain = subdomainService.getSubdomain();
        this.isParcelTracker = this.subdomain === "parcels";
        this.isEip = this.subdomain === "eip";
        this.isMainSite = this.subdomain === "main";
        // Auth0 SDK auto-initializes via provideAuth0() in app.config.ts.
        // AuthenticationService subscribes to auth0.user$ in its constructor and
        // hydrates the current user via POST /user-claims (or via the e2e shortcut
        // when localStorage.__e2e_globalID is set).
    }

    ngOnInit() {
        this.isIframe = window !== window.parent && !window.opener;
        const environmentClassName = environment.production ? "env-prod" : environment.staging ? "env-qa" : "env-dev";
        this.renderer.addClass(this._document.body, environmentClassName);

        this.router.events.subscribe((event: any) => {
            if (event instanceof RouteConfigLoadStart) {
                // lazy loaded route started
                this.busyService.setBusy(true);
            } else if (event instanceof RouteConfigLoadEnd) {
                // lazy loaded route ended
                this.busyService.setBusy(false);
            } else if (event instanceof NavigationEnd) {
                window.scrollTo(0, 0);
                this.isHomePage = event.urlAfterRedirects === "/" || event.urlAfterRedirects === "" || event.urlAfterRedirects.startsWith("/?");
            }
        });

        this.titleService.setTitle(`Lake Tahoe Info`);
        this.setAppFavicon();
        this.setupModalFocusTrap();
    }

    setAppFavicon() {
        this._document.getElementById("appFavicon").setAttribute("href", getAreaFaviconPath(this.subdomain));
    }

    private setupModalFocusTrap(): void {
        this.dialogService.hasOpenDialogs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((hasOpen) => {
            const host = this.elementRef.nativeElement as HTMLElement;
            const footer = host.querySelector("footer.home-footer");
            const routerOutlet = host.querySelector("router-outlet");
            const routedContent = routerOutlet?.nextElementSibling;
            const targets = [routedContent, footer].filter(Boolean);

            if (hasOpen) {
                this.previouslyFocusedElement = this._document.activeElement as HTMLElement;
                targets.forEach((el) => this.renderer.setAttribute(el, "inert", ""));
            } else {
                targets.forEach((el) => this.renderer.removeAttribute(el, "inert"));
                this.previouslyFocusedElement?.focus();
                this.previouslyFocusedElement = null;
            }
        });
    }
}
